import { useState, useEffect } from 'react';
import type { SentAssessment } from '../types/types';
import { supabase } from '../lib/supabase';

export const useSentAssessments = (
  organisationId: string | undefined,
  supplierId?: string,
  getSelfAssessments?: boolean,
  completedOnly?: boolean
) => {
  const [data, setData] = useState<SentAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSent = async () => {
    if (!organisationId) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    let query = supabase
      .from('assessments')
      .select(
        `
    id,
    status,
    created_at,
    submitted_at,
    completed_at,
    score,
    template_version,
    total_score,
    max_score,
    suppliers!assessments_supplier_id_fkey (company_name),
    questionnaire_templates!assessments_template_id_fkey (title),
    published_report:published_reports!published_reports_assessment_id_fkey (
      id,
      status,
      published_at
    )
  `
      )
      .or(
        `organisation_id.eq.${organisationId},supplier_id.eq.${organisationId}`
      )
      .eq('deleted', false);

    if (completedOnly) {
      query = query.eq('status', 'completed');
    }

    if (getSelfAssessments) {
      query = query.eq('is_self_assessment', true);
    } else {
      query = query.neq('is_self_assessment', true);
    }

    if (supplierId) {
      query = query.eq('supplier_id', supplierId);
    }

    const { data, error } = await query.order('created_at', {
      ascending: false,
    });

    if (error) {
      setError(error.message);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formatted = data.map((a: any) => {
        return {
          id: a.id,
          supplier_name: a.suppliers?.company_name || 'Unknown Supplier',
          template_name: a.questionnaire_templates?.title || 'Unknown Template',
          status: a.status,
          created_at: a.created_at,
          submitted_at: a.submitted_at,
          completed_at: a.completed_at,
          max_score: a.max_score,
          total_score: a.total_score,
          template_version: a.template_version,
          is_report_published: !!a.published_report,
          published_report: a.published_report,
        };
      });

      setData(formatted);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSent();
  }, [organisationId, supplierId]);

  return { data, loading, error, refetch: fetchSent };
};
